import React from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { createOutline, trashOutline } from 'ionicons/icons';
import { ActionGuard } from '@/components/ActionGuard';
import { ActionGuardContainer } from '@/components/ActionGuardContainer';
import OverflowMenu from '@/components/OverflowMenu';
import { ACTIONS, RESOURCES } from '@/contexts/CasbinContext';
import { topicGuardRegistrationStyle } from './styles';

interface TopicOverflowMenuProps {
  topicId: string;
  deleting: boolean;
  onEdit: () => void;
  onRequestDelete: () => void;
}

/**
 * Edit/Delete overflow menu for the topic manager, anchored to the top-right of
 * the title. The visible `OverflowMenu` is the container's group indicator; the
 * hidden probe `ActionGuard`s below feed the container so it knows whether ANY
 * action is allowed (they must stay descendants of the same container).
 */
const TopicOverflowMenu: React.FC<TopicOverflowMenuProps> = ({
  topicId,
  deleting,
  onEdit,
  onRequestDelete,
}) => (
  <ActionGuardContainer
    groupIndicatorConfig={{
      anyActionElem: (
        <OverflowMenu
          buttonId="topic-actions-menu-button"
          ariaLabel="Topic Topics"
          buttonTestId="topic-actions-menu-button"
        >
          {({ close, menuItemStyle }) => (
            <>
              <ActionGuard
                disableMode="hidden"
                action={ACTIONS.UPDATE}
                resource={RESOURCES.TOPICS}
                resourceId={topicId}
                helpText={{
                  singleVerification: 'Verify yourself to edit this topic',
                  updateProfile: 'Update your profile to edit this topic',
                  setupProfile: 'Set up your profile to edit this topic',
                }}
                guardedElem={
                  <IonButton
                    expand="block"
                    fill="clear"
                    onClick={() => {
                      close();
                      onEdit();
                    }}
                    aria-label="Edit Topic"
                    data-testid="edit-topic-menu-item"
                    style={menuItemStyle}
                  >
                    <IonIcon icon={createOutline} slot="start" />
                    Edit Topic
                  </IonButton>
                }
              />
              <ActionGuard
                disableMode="hidden"
                action={ACTIONS.DELETE}
                resource={RESOURCES.TOPICS}
                resourceId={topicId}
                helpText={{
                  singleVerification: 'Verify yourself to delete this topic',
                  updateProfile: 'Update your profile to delete this topic',
                  setupProfile: 'Set up your profile to delete this topic',
                }}
                guardedElem={
                  <IonButton
                    expand="block"
                    fill="clear"
                    color="danger"
                    onClick={() => {
                      close();
                      onRequestDelete();
                    }}
                    disabled={deleting}
                    aria-label="Delete Topic"
                    data-testid="delete-topic-menu-item"
                    style={menuItemStyle}
                  >
                    <IonIcon icon={trashOutline} slot="start" />
                    Delete Topic
                  </IonButton>
                }
              />
            </>
          )}
        </OverflowMenu>
      ),
    }}
  >
    <div style={topicGuardRegistrationStyle}>
      <ActionGuard
        disableMode="hidden"
        action={ACTIONS.UPDATE}
        resource={RESOURCES.TOPICS}
        resourceId={topicId}
        helpText={{
          singleVerification: 'Verify yourself to edit this topic',
          updateProfile: 'Update your profile to edit this topic',
          setupProfile: 'Set up your profile to edit this topic',
        }}
        guardedElem={<span />}
      />
      <ActionGuard
        disableMode="hidden"
        action={ACTIONS.DELETE}
        resource={RESOURCES.TOPICS}
        resourceId={topicId}
        helpText={{
          singleVerification: 'Verify yourself to delete this topic',
          updateProfile: 'Update your profile to delete this topic',
          setupProfile: 'Set up your profile to delete this topic',
        }}
        guardedElem={<span />}
      />
    </div>
  </ActionGuardContainer>
);

export default TopicOverflowMenu;
